import { useQuery, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import { contractsApi, type PaginatedContractsResponse } from "@/lib/api";
import type { Contract } from "@/types";
import { useOrganizationStore } from "@/stores/organizationStore";
import type {
  ContractEligibility,
  ContractStatus,
  ContractSummaryView,
  ContractType,
} from "@/types";

import { DEFAULT_PAGE_SIZE, PREFETCH_STALE_TIME } from "@/lib/constants";

interface ContractsFilters {
  search?: string;
  pageSize?: number;
  page?: number;
  status?: ContractStatus[];
  contractType?: ContractType[];
  ordering?: string;
  withoutTracks?: boolean;
  startDateAfter?: string;
  startDateBefore?: string;
  endDateAfter?: string;
  endDateBefore?: string;
  endDateActive?: boolean;
  summary?: ContractSummaryView;
  /** statement_picker only: bulk-statement eligibility for a period. */
  periodStart?: string;
  periodEnd?: string;
  /** API filter: keep one bucket (eligible / ineligible); omit → all. */
  eligibility?: ContractEligibility;
  /** statement_picker only: partition the eligible bucket by the run's selection. */
  selected?: boolean;
  /** With `selected`: explicit selection (include mode). */
  contractIds?: string[];
  /** With `selected` and no contractIds: select-all minus these (exclude mode). */
  deselectedContractIds?: string[];
}

interface UseContractsOptions extends ContractsFilters {
  enabled?: boolean;
  staleTime?: number;
}

interface ContractsQueryOptionsInput extends ContractsFilters {
  labelId: string | undefined;
  staleTime?: number;
}

interface UseContractsReturn {
  contracts: Contract[];
  count: number;
  next: string | null;
  previous: string | null;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  isSuccess: boolean;
  refetch: () => void;
}

// Helper function to create normalized query keys
const createContractsQueryKey = (
  labelId: string | undefined,
  search: string | undefined,
  pageSize: number | undefined,
  page: number | undefined,
  status: ContractStatus[] | undefined,
  contractType: ContractType[] | undefined,
  ordering: string | undefined,
  withoutTracks: boolean | undefined,
  startDateAfter: string | undefined,
  startDateBefore: string | undefined,
  endDateAfter: string | undefined,
  endDateBefore: string | undefined,
  endDateActive: boolean | undefined,
  summary: ContractSummaryView | undefined,
  periodStart: string | undefined,
  periodEnd: string | undefined,
  eligibility: ContractEligibility | undefined,
  selected: boolean | undefined,
  contractIds: string[] | undefined,
  deselectedContractIds: string[] | undefined,
) => {
  const key: (string | number)[] = ["contracts"];

  // Always include labelId (required for most queries)
  if (labelId) key.push("label", labelId);

  // Normalize search - convert undefined/empty to consistent value
  const normalizedSearch = search?.trim() || null;
  if (normalizedSearch) key.push("search", normalizedSearch);

  // Add pageSize if specified and different from default
  if (pageSize && pageSize !== DEFAULT_PAGE_SIZE) key.push("pageSize", pageSize);

  if (page && page > 1) key.push("page", page);

  // Add filter parameters
  if (status && status.length > 0) key.push("status", status.sort().join(","));
  if (contractType && contractType.length > 0)
    key.push("contractType", contractType.sort().join(","));
  if (ordering) key.push("ordering", ordering);
  if (withoutTracks) key.push("withoutTracks", "true");
  if (startDateAfter) key.push("startDateAfter", startDateAfter);
  if (startDateBefore) key.push("startDateBefore", startDateBefore);
  if (endDateAfter) key.push("endDateAfter", endDateAfter);
  if (endDateBefore) key.push("endDateBefore", endDateBefore);
  if (endDateActive) key.push("endDateActive", "true");
  if (summary) key.push("summary", summary);
  if (periodStart && periodEnd) key.push("period", periodStart, periodEnd);
  if (eligibility) key.push("eligibility", eligibility);
  if (selected !== undefined) key.push("selected", String(selected));
  if (contractIds?.length) key.push("contractIds", contractIds.join(","));
  if (deselectedContractIds?.length)
    key.push("deselectedContractIds", deselectedContractIds.join(","));

  return key;
};

export const contractsQueryOptions = ({
  labelId,
  staleTime,
  search,
  pageSize,
  page,
  status,
  contractType,
  ordering,
  withoutTracks,
  startDateAfter,
  startDateBefore,
  endDateAfter,
  endDateBefore,
  endDateActive,
  summary,
  periodStart,
  periodEnd,
  eligibility,
  selected,
  contractIds,
  deselectedContractIds,
}: ContractsQueryOptionsInput) => {
  const filters = {
    label_id: labelId,
    ...(search && { q: search }),
    ...(pageSize && { page_size: pageSize }),
    ...(page && { page }),
    ...(status && status.length > 0 && { status: status.join(",") }),
    ...(contractType && contractType.length > 0 && { contract_type: contractType.join(",") }),
    ...(ordering && { ordering }),
    ...(withoutTracks && { without_tracks: true }),
    ...(startDateAfter && { start_date_after: startDateAfter }),
    ...(startDateBefore && { start_date_before: startDateBefore }),
    ...(endDateAfter && { end_date_after: endDateAfter }),
    ...(endDateBefore && { end_date_before: endDateBefore }),
    ...(endDateActive && { end_date_active: true }),
    ...(summary && { summary }),
    ...(periodStart && periodEnd && { period_start: periodStart, period_end: periodEnd }),
    // Omitting `eligibility` returns every bucket mixed together.
    ...(eligibility && { eligibility }),
    ...(selected !== undefined && { selected }),
    ...(contractIds?.length && { contract_ids: contractIds.join(",") }),
    ...(deselectedContractIds?.length && {
      deselected_contract_ids: deselectedContractIds.join(","),
    }),
  };

  const queryKey = createContractsQueryKey(
    labelId,
    search,
    pageSize,
    page,
    status,
    contractType,
    ordering,
    withoutTracks,
    startDateAfter,
    startDateBefore,
    endDateAfter,
    endDateBefore,
    endDateActive,
    summary,
    periodStart,
    periodEnd,
    eligibility,
    selected,
    contractIds,
    deselectedContractIds,
  );

  return {
    queryKey,
    queryFn: (): Promise<PaginatedContractsResponse> => contractsApi.getContracts(filters),
    ...(staleTime !== undefined && { staleTime }),
  };
};

export const usePrefetchContracts = () => {
  const queryClient = useQueryClient();
  const { currentLabel } = useOrganizationStore();

  return (filters: ContractsFilters) => {
    if (!currentLabel) return;

    queryClient.prefetchQuery(
      contractsQueryOptions({
        ...filters,
        labelId: currentLabel.id,
        staleTime: PREFETCH_STALE_TIME,
      }),
    );
  };
};

export const useContracts = ({
  enabled = true,
  ...filters
}: UseContractsOptions = {}): UseContractsReturn => {
  const { currentLabel } = useOrganizationStore();

  const { data, isLoading, isError, error, isSuccess, refetch } = useQuery({
    ...contractsQueryOptions({ ...filters, labelId: currentLabel?.id }),
    enabled: enabled && !!currentLabel,
    placeholderData: keepPreviousData,
  });

  return {
    contracts: data?.results ?? [],
    count: data?.count ?? 0,
    next: data?.next ?? null,
    previous: data?.previous ?? null,
    isLoading,
    isError,
    error,
    isSuccess,
    refetch,
  };
};
